<template>
    <!-- Raiz é o próprio <video>: classe, estilo, controles e listeners do pai caem
         nele sem wrapper, então trocar `<video>` por `<VideoDeChat>` não mexe em layout. -->
    <video ref="videoRef" v-liberar-midia
        :src="srcEfetivo"
        :preload="preloadEfetivo"
        :poster="posterEfetivo"></video>
</template>

<script setup lang="ts">
/**
 * `<video>` de conversa que não prende conexão ao S3 no Firefox.
 *
 * Fora do Firefox é um `<video>` comum. No Firefox o vídeo não fala com o S3:
 * quando entra na tela, entra na `filaDeVideos`, que baixa o arquivo INTEIRO e
 * devolve uma blob URL — ver o porquê em `utils/video/filaDeVideos.ts`. Enquanto
 * espera, o `poster` mostra a fila e o progresso. Vídeo grande demais para
 * memória, ou que o fetch não alcança (CORS), cai para a URL direta com
 * `preload="none"`: não prende nada até o operador dar play.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { ehFirefox } from '@/utils/navegador';
import { filaDeVideos, type Reserva } from '@/utils/video/filaDeVideos';

const props = defineProps<{
    src?: string | null;
    preload?: '' | 'none' | 'metadata' | 'auto';
    poster?: string;
}>();

const firefox = ehFirefox();
const videoRef = ref<HTMLVideoElement | null>(null);
const blobUrl = ref<string | null>(null);
const usarUrlDireta = ref(false);
/** `null` fora da fila; 0..100 enquanto espera/baixa. */
const progresso = ref<number | null>(null);

let reserva: Reserva | null = null;
let visivel = false;
let observador: IntersectionObserver | null = null;

const srcEfetivo = computed(() => {
    if (!firefox) return props.src ?? undefined;
    return blobUrl.value ?? (usarUrlDireta.value ? props.src ?? undefined : undefined);
});

const preloadEfetivo = computed(() => {
    if (!firefox) return props.preload;
    // Blob é local: carregar metadados não abre conexão nenhuma.
    if (blobUrl.value) return props.preload || 'metadata';
    return 'none';
});

function posterDeEspera(pct: number): string {
    const texto = pct > 0 ? `Carregando vídeo ${pct}%` : 'Vídeo na fila…';
    const svg =
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 180">` +
        `<rect width="320" height="180" fill="#1f1f1f"/>` +
        `<rect x="60" y="112" width="200" height="4" rx="2" fill="#3a3a3a"/>` +
        `<rect x="60" y="112" width="${2 * pct}" height="4" rx="2" fill="#9aa0a6"/>` +
        `<text x="160" y="98" fill="#d0d0d0" font-family="sans-serif" font-size="16" text-anchor="middle">${texto}</text>` +
        `</svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

const posterEfetivo = computed(() => {
    if (props.poster) return props.poster;
    if (!firefox || blobUrl.value || usarUrlDireta.value || progresso.value === null) return undefined;
    return posterDeEspera(progresso.value);
});

function soltarReserva() {
    reserva?.liberar();
    reserva = null;
    progresso.value = null;
}

function reservarSePreciso() {
    const url = props.src;
    if (!url || reserva || blobUrl.value || usarUrlDireta.value) return;
    // Blob/objeto local (prévia antes do upload) não passa pelo S3.
    if (!/^https?:/.test(url)) { usarUrlDireta.value = true; return; }

    const atual = filaDeVideos.reservar(url, (fracao) => {
        const pct = Math.round(fracao * 100);
        if (reserva === atual && pct !== progresso.value) progresso.value = pct;
    });
    reserva = atual;
    progresso.value = 0;
    atual.pronto
        .then((u) => { if (reserva === atual) { blobUrl.value = u; progresso.value = null; } })
        .catch((erro) => {
            if (reserva !== atual) return;
            if (erro instanceof DOMException && erro.name === 'AbortError') return;
            usarUrlDireta.value = true;
            progresso.value = null;
        });
}

onMounted(() => {
    if (!firefox || !videoRef.value) return;
    observador = new IntersectionObserver((entradas) => {
        visivel = entradas.some((e) => e.isIntersecting);
        if (visivel) reservarSePreciso();
        // Saiu da tela antes de começar: sai da fila e dá a vez a quem está à vista.
        // Download já em curso continua — terminar é o que devolve a conexão.
        else if (reserva?.estado() === 'fila') soltarReserva();
    }, { rootMargin: '200px' });
    observador.observe(videoRef.value);
});

watch(() => props.src, () => {
    if (!firefox) return;
    soltarReserva();
    blobUrl.value = null;
    usarUrlDireta.value = false;
    if (visivel) reservarSePreciso();
});

onBeforeUnmount(() => {
    observador?.disconnect();
    soltarReserva();
});
</script>
